import { useState } from "react";
import { toast } from "react-toastify";
import { FiSend, FiAlertTriangle, FiUserCheck, FiClock, FiSlash, FiEye } from "react-icons/fi";
import { Card, Button, Modal, FormField, DataTable, StatusBadge, Banner } from "../../../components/common";
import useAsync from "../../../hooks/useAsync";
import { getLookups } from "../../../services/employeeApi";
import { releaseResult } from "../../../services/resultApi";
import {
  setEligibility, changeReviewer, overrideDeadline, raiseException, resolveException, remindRecord,
} from "../../../services/recordApi";
import { getErrorMessage } from "../../../utils/errors";
import { formatDateTime, todayISO } from "../../../utils/format";
import Select from "../../../components/common/Select"; // [NEW] alphabetical dropdown (replaces <select>)

const api = { getLookups, releaseResult, setEligibility, changeReviewer, overrideDeadline, raiseException, resolveException, remindRecord };

const STAGES = [
  { value: "GOAL_SETTING", label: "Goal setting" },
  { value: "SELF_ASSESSMENT", label: "Self assessment" },
  { value: "MANAGER_ASSESSMENT", label: "Manager assessment" },
  { value: "HR_FINALIZATION", label: "HR finalization" },
];
const ELIGIBILITY = [
  { value: "ELIGIBLE", label: "Eligible" },
  { value: "EXCLUDED", label: "Excluded" },
  { value: "NOT_ELIGIBLE", label: "Not eligible" },
  { value: "EXITED", label: "Exited" },
];

export default function RecordAdminCard({ data, cycleId, employeeId, onChanged }) {
  const [dialog, setDialog] = useState(null);
  const [busy, setBusy] = useState(false);
  const people = useAsync(() => api.getLookups(), [dialog?.kind === "reviewer"], { skip: dialog?.kind !== "reviewer" });

  const run = async (fn, message) => {
    setBusy(true);
    try {
      await fn();
      toast.success(message);
      setDialog(null);
      onChanged();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const submit = () => {
    const d = dialog;
    if (!String(d.reason || "").trim() && d.kind !== "exception") { toast.error("Give a reason; it is stored in the audit trail."); return; }
    switch (d.kind) {
      case "release": return run(() => api.releaseResult(cycleId, employeeId, d.reason), "Result released to the employee.");
      case "eligibility": return run(() => api.setEligibility(cycleId, employeeId, d.value, d.reason), "Eligibility updated.");
      case "reviewer":
        if (!d.value) { toast.error("Choose the new reviewer."); return null; }
        return run(() => api.changeReviewer(cycleId, employeeId, d.value, d.reason), "Reviewer changed.");
      case "deadline":
        if (!d.opensOn || !d.closesOn || d.closesOn < d.opensOn) { toast.error("Pick a valid window."); return null; }
        return run(() => api.overrideDeadline(cycleId, employeeId, { stage: d.value, opensOn: d.opensOn, closesOn: d.closesOn, reason: d.reason }), "Deadline override saved.");
      case "exception":
        if (!String(d.code || "").trim() || !String(d.description || "").trim()) { toast.error("Add a code and a description."); return null; }
        return run(() => api.raiseException(cycleId, employeeId, { code: d.code, severity: d.value, description: d.description, reason: d.reason }), "Exception raised.");
      case "resolve": return run(() => api.resolveException(d.exceptionId, d.value, d.reason), "Exception updated.");
      case "remind": return run(() => api.remindRecord(cycleId, employeeId, d.reason), "Reminder sent to whoever owns the current step.");
      default: return null;
    }
  };

  const canRelease = data.result.status === "Finalized" && !data.result.released;
  const titles = {
    release: "Release result to the employee",
    eligibility: "Change eligibility",
    reviewer: "Change reviewer",
    deadline: "Deadline override",
    exception: "Raise an exception",
    resolve: "Resolve exception",
    remind: "Send a reminder",
  };

  return (
    <>
      <Card title="Record administration" subtitle={`Eligibility: ${data.eligibility.replace("_", " ").toLowerCase()}. Reviewer: ${data.employee.managerName}.`}>
        <div className="decision-bar">
          {canRelease && <Button icon={FiEye} onClick={() => setDialog({ kind: "release", reason: "Result released after HR review" })}>Release result</Button>}
          <Button variant="secondary" icon={FiSend} onClick={() => setDialog({ kind: "remind", reason: "Please complete your pending step" })}>Send reminder</Button>
          <Button variant="secondary" icon={FiClock} disabled={data.isFinal} onClick={() => setDialog({ kind: "deadline", value: "SELF_ASSESSMENT", opensOn: todayISO(), closesOn: todayISO(), reason: "" })}>Deadline override</Button>
          <Button variant="secondary" icon={FiUserCheck} disabled={data.isFinal} onClick={() => setDialog({ kind: "reviewer", value: "", reason: "" })}>Change reviewer</Button>
          <Button variant="ghost" icon={FiAlertTriangle} onClick={() => setDialog({ kind: "exception", value: "BLOCKING", code: "", description: "", reason: "" })}>Raise exception</Button>
          <Button variant="ghost" icon={FiSlash} disabled={data.isFinal} onClick={() => setDialog({ kind: "eligibility", value: data.eligibility, reason: "" })}>Eligibility</Button>
        </div>
        {data.readiness && !data.readiness.ready && data.result.status !== "Finalized" && (
          <>
            <p className="detail-label">Before finalization</p>
            <ul className="icon-list">{data.readiness.issues.map((i) => <li key={i}>{i}</li>)}</ul>
          </>
        )}
      </Card>

      {data.exceptions.length > 0 && (
        <Card title="Exceptions" subtitle="Open blocking exceptions stop finalization." padded={false}>
          <DataTable keepOrder
            dense
            rows={data.exceptions}
            columns={[
              { key: "code", label: "Code", render: (r) => <span className="cell-stack"><strong>{r.code}</strong><small>{formatDateTime(r.raisedAt)}</small></span> },
              { key: "severity", label: "Severity", render: (r) => <StatusBadge status={r.severity} tone={r.severity === "BLOCKING" ? "danger" : "warning"} /> },
              { key: "description", label: "Description" },
              { key: "status", label: "Status", render: (r) => <StatusBadge status={r.status} tone={r.status === "OPEN" ? "warning" : "success"} /> },
              {
                key: "act", label: "", align: "right",
                render: (r) => r.status === "OPEN" && <Button size="sm" variant="secondary" onClick={() => setDialog({ kind: "resolve", exceptionId: r.id, value: "RESOLVED", reason: "" })}>Resolve</Button>,
              },
            ]}
          />
        </Card>
      )}

      {data.result.revisions.length > 1 && (
        <Card title="Result revisions" padded={false}>
          <DataTable keepOrder
            dense
            rows={data.result.revisions}
            columns={[
              { key: "revisionNo", label: "Revision" },
              { key: "finalScore", label: "Score", align: "right" },
              { key: "rating", label: "Rating" },
              { key: "finalizedAt", label: "Finalized", render: (r) => formatDateTime(r.finalizedAt) },
              { key: "releasedAt", label: "Released", render: (r) => (r.releasedAt ? formatDateTime(r.releasedAt) : "Not released") },
              { key: "correctionReason", label: "Correction reason", render: (r) => r.correctionReason || "—" },
            ]}
          />
        </Card>
      )}

      <Modal
        open={Boolean(dialog)}
        size="sm"
        title={titles[dialog?.kind]}
        subtitle="Recorded in the audit trail."
        onClose={busy ? undefined : () => setDialog(null)}
        footer={<><Button variant="ghost" onClick={() => setDialog(null)} disabled={busy}>Cancel</Button><Button onClick={submit} loading={busy}>Confirm</Button></>}
      >
        {dialog && (
          <div className="form-stack">
            {dialog.kind === "eligibility" && (
              <FormField label="Eligibility">
                <Select value={dialog.value} onChange={(e) => setDialog({ ...dialog, value: e.target.value })}>
                  {ELIGIBILITY.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </Select>
              </FormField>
            )}
            {dialog.kind === "reviewer" && (
              <FormField label="New reviewer" hint="Must be an active employee other than the reviewee.">
                <Select value={dialog.value} onChange={(e) => setDialog({ ...dialog, value: e.target.value })}>
                  <option value="">Choose a person</option>
                  {(people.data?.managers || []).filter((m) => m.id !== employeeId && m.id !== data.reviewerId).map((m) => <option key={m.id} value={m.id}>{m.name} ({m.id})</option>)}
                </Select>
              </FormField>
            )}
            {dialog.kind === "reviewer" && data.managerAssessment && (
              <Banner tone="warning" title="A manager assessment already exists">
                {data.managerAssessment.status === "Submitted"
                  ? `It was submitted by ${data.managerAssessment.managerName}. The new reviewer does not redo it; the system will keep recording it under the reviewer who submitted it. Tell both people about the change.`
                  : "The new reviewer continues from the saved draft. Tell the previous reviewer and the employee about the change; the system does not notify them."}
              </Banner>
            )}
            {dialog.kind === "deadline" && (
              <>
                <p className="form-hint">The extension is shown on this person&apos;s screens, so they can act after the cycle deadline.</p>
                <FormField label="Stage">
                  <Select keepOrder value={dialog.value} onChange={(e) => setDialog({ ...dialog, value: e.target.value })}>
                    {STAGES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </Select>
                </FormField>
                <div className="form-grid">
                  <FormField label="Opens on"><input type="date" value={dialog.opensOn} onChange={(e) => setDialog({ ...dialog, opensOn: e.target.value })} /></FormField>
                  <FormField label="Closes on"><input type="date" value={dialog.closesOn} onChange={(e) => setDialog({ ...dialog, closesOn: e.target.value })} /></FormField>
                </div>
              </>
            )}
            {dialog.kind === "exception" && (
              <>
                <div className="form-grid">
                  <FormField label="Code" required><input value={dialog.code} onChange={(e) => setDialog({ ...dialog, code: e.target.value.toUpperCase() })} placeholder="DATA_MISMATCH" /></FormField>
                  <FormField label="Severity">
                    <Select value={dialog.value} onChange={(e) => setDialog({ ...dialog, value: e.target.value })}>
                      <option value="BLOCKING">Blocking (stops finalization)</option>
                      <option value="ADVISORY">Advisory</option>
                    </Select>
                  </FormField>
                </div>
                <FormField label="Description" required><textarea rows={2} value={dialog.description} onChange={(e) => setDialog({ ...dialog, description: e.target.value })} /></FormField>
              </>
            )}
            {dialog.kind === "resolve" && (
              <FormField label="Outcome">
                <Select value={dialog.value} onChange={(e) => setDialog({ ...dialog, value: e.target.value })}>
                  <option value="RESOLVED">Resolved</option>
                  <option value="WAIVED">Waived</option>
                </Select>
              </FormField>
            )}
            <FormField label={dialog.kind === "remind" ? "Message" : "Reason"} required={dialog.kind !== "exception"}>
              <textarea rows={2} value={dialog.reason} onChange={(e) => setDialog({ ...dialog, reason: e.target.value })} />
            </FormField>
          </div>
        )}
      </Modal>
    </>
  );
}
